import React, {FC, useEffect, useState} from 'react'
import {useFormik} from 'formik'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {ICorporateModel} from '../../admin/corporate/CorporateModel'
import {apiErrorMessage, companyPath} from '../../public/PublicModels'
import {getCompanyProfile, updateCompanyProfile} from './Api'
import {
  EMPLOYEE_COUNT_OPTIONS,
  ICompanyProfileRequest,
  toCompanyProfileForm,
  validationCompanyProfile,
} from './CompanyProfileModel'
import {ImageUploadField} from './ImageUploadField'

const emptyForm: ICompanyProfileRequest = {
  tagline: '',
  about: '',
  industry: '',
  employeeCount: '',
  country: '',
  website: '',
  linkedinUrl: '',
  termsUrl: '',
  privacyUrl: '',
  logoDocumentId: '',
  headerDocumentId: '',
}

/** Firma kullanıcısının kariyer sayfası bilgileri (logo, header, tanıtım, bağlantılar) */
const CompanyProfilePage: FC = () => {
  const [corporate, setCorporate] = useState<ICorporateModel | null>(null)
  const [loading, setLoading] = useState(true)

  const formik = useFormik<ICompanyProfileRequest>({
    initialValues: emptyForm,
    validationSchema: validationCompanyProfile,
    onSubmit: async (values, {setSubmitting}) => {
      try {
        const {data} = await updateCompanyProfile(values)
        setCorporate(data.payload)
        showSuccessToast('Firma bilgileri kaydedildi')
      } catch (error) {
        showErrorToast(apiErrorMessage(error, 'Firma bilgileri kaydedilemedi'))
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    getCompanyProfile()
      .then(({data}) => {
        setCorporate(data.payload)
        formik.resetForm({values: toCompanyProfileForm(data.payload)})
      })
      .catch((error) => showErrorToast(apiErrorMessage(error, 'Firma bilgileri yüklenemedi')))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fieldError = (field: keyof ICompanyProfileRequest) =>
    formik.touched[field] && formik.errors[field] ? formik.errors[field] : undefined

  const renderInput = (field: keyof ICompanyProfileRequest, label: string, placeholder: string) => (
    <>
      <label className='form-label fw-bold'>{label}</label>
      <input
        type='text'
        className={`form-control ${fieldError(field) ? 'is-invalid' : ''}`}
        placeholder={placeholder}
        {...formik.getFieldProps(field)}
      />
      {fieldError(field) && <div className='invalid-feedback'>{fieldError(field)}</div>}
    </>
  )

  return (
    <>
      <PageTitle breadcrumbs={[]}>Firma Profili</PageTitle>

      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='card mb-6'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bolder fs-3 mb-1'>{corporate?.name || 'Firma Profili'}</span>
              <span className='text-muted mt-1 fw-bold fs-7'>
                Bu bilgiler adayların gördüğü kariyer sayfasında yayınlanır
              </span>
            </h3>
            <div className='card-toolbar gap-2'>
              {corporate?.slug && (
                <a
                  href={companyPath(corporate.slug)}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='btn btn-sm btn-light-info'
                >
                  <i className='bi bi-box-arrow-up-right'></i>
                  Kariyer Sayfasını Aç
                </a>
              )}
              <button
                type='submit'
                className='btn btn-sm btn-primary'
                disabled={loading || formik.isSubmitting}
              >
                {formik.isSubmitting ? (
                  <span className='spinner-border spinner-border-sm' />
                ) : (
                  <>
                    <i className='bi bi-check-lg'></i>
                    Kaydet
                  </>
                )}
              </button>
            </div>
          </div>

          {loading ? (
            <div className='card-body d-flex justify-content-center py-20'>
              <span className='spinner-border text-primary' />
            </div>
          ) : (
            <div className='card-body pt-2'>
              {!corporate?.slug && (
                <div className='alert alert-warning'>
                  Kariyer sayfası adresiniz henüz tanımlanmamış. Sistem yöneticinizle iletişime geçiniz.
                </div>
              )}

              <div className='row g-8 mb-8'>
                <div className='col-md-3'>
                  <label className='form-label fw-bold'>Logo</label>
                  <ImageUploadField
                    category='LOGO'
                    variant='logo'
                    documentId={formik.values.logoDocumentId}
                    onChange={(id) => formik.setFieldValue('logoDocumentId', id)}
                    hint='Kare görsel önerilir. PNG, JPG, WEBP · en fazla 5MB'
                  />
                </div>
                <div className='col-md-9'>
                  <label className='form-label fw-bold'>Üst Görsel (Header)</label>
                  <ImageUploadField
                    category='HEADER'
                    variant='header'
                    documentId={formik.values.headerDocumentId}
                    onChange={(id) => formik.setFieldValue('headerDocumentId', id)}
                    hint='Geniş görsel önerilir (örn. 1920×400). PNG, JPG, WEBP · en fazla 5MB'
                  />
                </div>
              </div>

              <div className='row g-5'>
                <div className='col-12'>
                  {renderInput('tagline', 'Kısa Tanıtım', 'Örn: Dünyanın her yerinde kullanılan mobil uygulamalar geliştiriyoruz.')}
                </div>
                <div className='col-md-4'>
                  {renderInput('industry', 'Sektör', 'Örn: Bilgi Teknolojileri ve Hizmetleri')}
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-bold'>Çalışan Sayısı</label>
                  <select className='form-select' {...formik.getFieldProps('employeeCount')}>
                    <option value=''>Seçiniz</option>
                    {EMPLOYEE_COUNT_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option} kişi
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-md-4'>{renderInput('country', 'Ülke', 'Örn: Türkiye')}</div>
                <div className='col-md-6'>{renderInput('website', 'Web Sitesi', 'https://www.firma.com')}</div>
                <div className='col-md-6'>
                  {renderInput('linkedinUrl', 'LinkedIn', 'https://www.linkedin.com/company/firma')}
                </div>
                <div className='col-12'>
                  <label className='form-label fw-bold'>Şirket Tanıtımı</label>
                  <textarea
                    rows={8}
                    className={`form-control ${fieldError('about') ? 'is-invalid' : ''}`}
                    placeholder='Şirketinizi, kültürünüzü ve çalışma ortamınızı adaylara anlatın. Paragrafları boş satırla ayırabilirsiniz.'
                    {...formik.getFieldProps('about')}
                  />
                  {fieldError('about') && <div className='invalid-feedback'>{fieldError('about')}</div>}
                </div>
              </div>
            </div>
          )}
        </div>

        {!loading && (
          <div className='card'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-4 mb-1'>Başvuru Formu Metinleri</span>
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Adayların başvuru sırasında onayladığı metinlerin bağlantıları
                </span>
              </h3>
            </div>
            <div className='card-body pt-2'>
              <div className='row g-5'>
                <div className='col-md-6'>
                  {renderInput('termsUrl', 'Kullanım Koşulları', 'https://www.firma.com/kosullar')}
                </div>
                <div className='col-md-6'>
                  {renderInput('privacyUrl', 'KVKK Aydınlatma Metni', 'https://www.firma.com/kvkk')}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {CompanyProfilePage}
